import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    BooleanInput,
    Create,
    Edit,
    FunctionField,
    List,
    NumberInput,
    SimpleForm,
    TextInput,
    required,
    useDataProvider,
    useNotify,
    useRecordContext,
    useRedirect,
    useListContext,
} from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';
import { Box, Button, Chip, CircularProgress, IconButton, Paper, Stack, Typography } from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CloseIcon from '@mui/icons-material/Close';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import StarsRoundedIcon from '@mui/icons-material/StarsRounded';
import { ImageCropDialog } from './components/ImageCropDialog';
import { QuestSectionTabs } from './components/QuestSectionTabs';
import { uploadQuestRewardImage, deleteFileFromS3 } from './s3Service';
import { CARD_PREVIEW_RADIUS } from './quests';

// Награда в приложении — та же квадратная карточка со скруглением, но крупнее задачи:
// экспортируем 640×640.
const REWARD_IMAGE_OUTPUT = 640;

// Индексы файлов reward_{index}_id_{id}.png — следующий берём как max+1,
// чтобы не перетирать существующие картинки.
const parseImageIndex = (url) => {
    const match = String(url).match(/reward_(\d+)_id_/);
    return match ? Number(match[1]) : -1;
};

const nextImageIndex = (urls) => (
    urls.reduce((max, url) => Math.max(max, parseImageIndex(url)), -1) + 1
);

// ── Картинки награды: несколько, каждая через кроппер ─────────────────────────
const RewardImagesInput = () => {
    const { setValue } = useFormContext();
    const imageUrls = useWatch({ name: 'image_urls' }) || [];
    const newFiles = useWatch({ name: '__new_image_files' }) || [];
    const removedUrls = useWatch({ name: '__removed_urls' }) || [];
    const [rawFile, setRawFile] = useState(null);
    const [cropOpen, setCropOpen] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [deletingUrl, setDeletingUrl] = useState(null);
    const notify = useNotify();
    const dataProvider = useDataProvider();
    // В форме создания записи ещё нет: без id некуда класть файл в S3 и нечего
    // патчить, поэтому там картинки по-прежнему уходят после «Сохранить».
    const record = useRecordContext();
    const isEdit = Boolean(record?.id);

    const newPreviews = useMemo(
        () => newFiles.map((file) => URL.createObjectURL(file)),
        [newFiles]
    );
    useEffect(() => () => {
        newPreviews.forEach((url) => URL.revokeObjectURL(url));
    }, [newPreviews]);

    const handleFileChange = (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;
        setRawFile(file);
        setCropOpen(true);
    };

    const handleCropApply = async (croppedFile, error) => {
        setCropOpen(false);
        setRawFile(null);
        if (error || !croppedFile) {
            notify(error?.message || 'Не удалось обрезать изображение', { type: 'error' });
            return;
        }
        if (!isEdit) {
            setValue('__new_image_files', [...newFiles, croppedFile], { shouldDirty: true });
            return;
        }

        setUploading(true);
        try {
            // Индекс считаем с учётом удалённых: иначе новый файл лёг бы на их ключ,
            // и браузер показывал бы старую картинку из кэша.
            const index = nextImageIndex([...imageUrls, ...removedUrls]);
            const url = await uploadQuestRewardImage(croppedFile, record.id, index);
            const urls = [...imageUrls, url];
            // Патчим серверными полями, а не формой: незасейвленные правки других
            // полей трогать не наше дело — сохраняем только картинки.
            await dataProvider.update('rewards', {
                id: record.id,
                data: { ...record, image_urls: urls },
                previousData: record,
            });
            setValue('image_urls', urls, { shouldDirty: false });
            notify('Изображение загружено и сохранено', { type: 'success' });
        } catch (uploadError) {
            notify(`Не удалось сохранить изображение: ${uploadError.message || ''}`, { type: 'error' });
        } finally {
            setUploading(false);
        }
    };

    const removeExisting = async (url) => {
        if (!isEdit) {
            setValue('image_urls', imageUrls.filter((u) => u !== url), { shouldDirty: true });
            setValue('__removed_urls', [...removedUrls, url], { shouldDirty: true });
            return;
        }
        if (!window.confirm('Удалить это изображение? Оно сразу пропадёт из приложения, отменить будет нельзя.')) {
            return;
        }

        setDeletingUrl(url);
        try {
            const urls = imageUrls.filter((u) => u !== url);
            // Сначала убираем ссылку на бэкенде, потом файл из S3: если удаление
            // файла упадёт, останется лишний файл — это лучше битой картинки.
            await dataProvider.update('rewards', {
                id: record.id,
                data: { ...record, image_urls: urls },
                previousData: record,
            });
            setValue('image_urls', urls, { shouldDirty: false });
            // Ссылку запоминаем, чтобы индекс ключа в S3 не ушёл на переиспользование.
            setValue('__removed_urls', [...removedUrls, url], { shouldDirty: false });
            try {
                await deleteFileFromS3(url);
            } catch (s3Error) {
                notify('Картинка убрана из награды, но файл в S3 удалить не удалось', { type: 'warning' });
                return;
            }
            notify('Изображение удалено', { type: 'success' });
        } catch (deleteError) {
            notify(`Не удалось удалить изображение: ${deleteError.message || ''}`, { type: 'error' });
        } finally {
            setDeletingUrl(null);
        }
    };

    const removeNew = (index) => {
        setValue('__new_image_files', newFiles.filter((_, i) => i !== index), { shouldDirty: true });
    };

    const thumbSx = {
        position: 'relative',
        width: 120,
        height: 120,
        borderRadius: CARD_PREVIEW_RADIUS,
        overflow: 'hidden',
        backgroundColor: 'action.hover',
        flexShrink: 0,
    };

    const removeButtonSx = {
        position: 'absolute',
        top: 4,
        right: 4,
        backgroundColor: 'rgba(0,0,0,0.55)',
        color: '#fff',
        '&:hover': { backgroundColor: 'rgba(0,0,0,0.75)' },
    };

    return (
        <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Изображения награды
            </Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                {imageUrls.map((url) => (
                    <Box key={url} sx={thumbSx}>
                        <img
                            src={url}
                            alt="Изображение награды"
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                        <IconButton
                            size="small"
                            sx={removeButtonSx}
                            disabled={deletingUrl === url}
                            onClick={() => removeExisting(url)}
                        >
                            {deletingUrl === url
                                ? <CircularProgress size={16} sx={{ color: '#fff' }} />
                                : <CloseIcon fontSize="small" />}
                        </IconButton>
                    </Box>
                ))}
                {newFiles.map((file, index) => (
                    <Box key={`new-${index}`} sx={thumbSx}>
                        <img
                            src={newPreviews[index]}
                            alt="Новое изображение"
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                        <IconButton size="small" sx={removeButtonSx} onClick={() => removeNew(index)}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                ))}
                <Button
                    variant="outlined"
                    component="label"
                    disabled={uploading}
                    sx={{ width: 120, height: 120, borderRadius: CARD_PREVIEW_RADIUS, flexDirection: 'column' }}
                >
                    {uploading ? <CircularProgress size={24} /> : <AddPhotoAlternateIcon />}
                    <Typography variant="caption">{uploading ? 'Загрузка…' : 'Добавить'}</Typography>
                    <input hidden type="file" accept="image/*" onChange={handleFileChange} />
                </Button>
            </Stack>
            <Typography variant="caption" color="text.secondary" display="block">
                Первая картинка показывается в списке наград приложения
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
                {isEdit
                    ? 'Добавление и удаление картинок применяются сразу, без «Сохранить».'
                    : 'Картинки загрузятся после создания награды.'}
            </Typography>

            <ImageCropDialog
                open={cropOpen}
                file={rawFile}
                title="Обрезка изображения награды"
                aspectRatio={1}
                outputWidth={REWARD_IMAGE_OUTPUT}
                outputHeight={REWARD_IMAGE_OUTPUT}
                previewBorderRadius={CARD_PREVIEW_RADIUS}
                previewMaxWidth={420}
                helperText="Выберите масштаб и положение — так картинка будет выглядеть в приложении"
                onCancel={() => {
                    setCropOpen(false);
                    setRawFile(null);
                }}
                onApply={handleCropApply}
            />
        </Box>
    );
};

const RewardForm = ({ isCreate = false }) => (
    <SimpleForm sx={{ '& .RaSimpleForm-stack': { width: '100%', maxWidth: 'none' } }}>
        <Box sx={{ width: '100%', mb: 2 }}><Typography variant="overline" color="primary" sx={{ fontWeight: 800 }}>Магазин наград</Typography><Typography variant="h4" sx={{ fontWeight: 850, letterSpacing: '-.03em' }}>{isCreate ? 'Новая награда' : 'Редактирование награды'}</Typography><Typography color="text.secondary" sx={{ mt: .75 }}>Оформите карточку, цену, остаток и правила получения.</Typography></Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 2fr) minmax(300px, 1fr)' }, gap: 3, width: '100%', alignItems: 'start' }}>
            <Box sx={{ display: 'grid', gap: 3 }}>
                <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>Описание награды</Typography>
                    <TextInput source="name" label="Название" validate={required()} fullWidth />
                    <TextInput source="description" label="Описание" multiline minRows={5} fullWidth />
                </Paper>
                <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, mb: .5 }}>Фотографии</Typography><Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Первое изображение станет обложкой в каталоге наград.</Typography><RewardImagesInput />
                </Paper>
            </Box>
            <Box sx={{ display: 'grid', gap: 3, position: { lg: 'sticky' }, top: { lg: 88 } }}>
                <Paper elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>Стоимость и остаток</Typography>
                    <NumberInput source="cost_points" label="Цена в очках" min={0} defaultValue={isCreate ? 0 : undefined} validate={required()} fullWidth />
                    <NumberInput source="stock_total" label="Количество в поставке" helperText={isCreate ? 'Например, 300 сувениров' : 'Увеличение считается новой поставкой'} min={0} defaultValue={isCreate ? 0 : undefined} validate={required()} fullWidth />
                    {!isCreate && <FunctionField label="Доступно сейчас" render={(record) => <Chip icon={<Inventory2RoundedIcon />} label={`${record.stock_available} шт. доступно`} color={record.stock_available > 0 ? 'success' : 'warning'} />} />}
                    <NumberInput source="max_per_user" label="Лимит на пользователя" helperText="Оставьте пустым, если лимита нет" min={1} fullWidth />
                </Paper>
                <Paper elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}><Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>Доступность</Typography><BooleanInput source="is_active" label="Награда активна" defaultValue={isCreate ? true : undefined} /><Typography variant="caption" color="text.secondary">Неактивная награда скрыта из каталога, но остаётся в истории покупок.</Typography></Paper>
            </Box>
        </Box>
    </SimpleForm>
);

const RewardCards = () => {
    const { data = [], isPending } = useListContext();
    if (isPending) return <Box sx={{ py: 8, display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>;
    return <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' }, gap: 2 }}>{data.map((record) => {
        const url = (record.image_urls || [])[0] || record.image_url;
        return <Paper key={record.id} component="a" href={`#/rewards/${record.id}`} elevation={0} sx={{ overflow: 'hidden', border: '1px solid', borderColor: 'divider', borderRadius: 3, color: 'inherit', textDecoration: 'none', transition: '160ms ease', '&:hover': { borderColor: 'primary.main', transform: 'translateY(-3px)', boxShadow: '0 14px 34px rgba(15,23,42,.1)' }, '&:focus-visible': { outline: '3px solid', outlineColor: 'primary.light', outlineOffset: 2 } }}>
            <Box sx={{ aspectRatio: '1', bgcolor: 'action.hover', display: 'grid', placeItems: 'center', overflow: 'hidden' }}>{url ? <Box component="img" src={url} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <Inventory2RoundedIcon sx={{ fontSize: 48, color: 'text.disabled' }} />}</Box>
            <Box sx={{ p: 2 }}><Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'center' }}><Chip size="small" label={record.is_active ? 'Активна' : 'Скрыта'} color={record.is_active ? 'success' : 'default'} /><Typography variant="caption" color="text.secondary">#{record.id}</Typography></Box><Typography variant="h6" sx={{ fontWeight: 800, mt: 1.25 }}>{record.name || 'Без названия'}</Typography><Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}><Chip size="small" icon={<StarsRoundedIcon />} label={`${record.cost_points || 0} очков`} color="primary" /><Typography variant="body2" color={record.stock_available > 0 ? 'text.secondary' : 'warning.main'}>{record.stock_available || 0} из {record.stock_total || 0}</Typography></Box></Box>
        </Paper>;
    })}</Box>;
};

export const RewardList = () => (
    <>
        <QuestSectionTabs />
        <List sort={{ field: 'created_at', order: 'DESC' }} title="Квесты" sx={{ '& .RaList-content': { boxShadow: 'none', bgcolor: 'transparent' } }}>
            <Box sx={{ mb: 3 }}><Typography variant="h4" sx={{ fontWeight: 850, letterSpacing: '-.03em', mb: .75 }}>Награды</Typography><Typography color="text.secondary">Каталог сувениров, которые посетители получают за заработанные очки.</Typography></Box>
            <RewardCards />
        </List>
    </>
);

// Создание: id появляется после ответа бэкенда, поэтому файлы грузим в S3 в onSuccess
// и сразу дополняем награду ссылками.
export const RewardCreate = () => {
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const redirect = useRedirect();
    const pendingFilesRef = useRef([]);

    const transform = (data) => {
        const { __new_image_files, __removed_urls, ...rest } = data;
        pendingFilesRef.current = __new_image_files || [];
        return rest;
    };

    const onSuccess = async (created) => {
        const files = pendingFilesRef.current;
        pendingFilesRef.current = [];
        if (files.length) {
            try {
                const urls = [];
                for (let i = 0; i < files.length; i += 1) {
                    // eslint-disable-next-line no-await-in-loop
                    urls.push(await uploadQuestRewardImage(files[i], created.id, i));
                }
                await dataProvider.update('rewards', {
                    id: created.id,
                    data: { ...created, image_urls: urls },
                    previousData: created,
                });
            } catch (error) {
                notify(`Награда создана, но изображения не загрузились: ${error.message || ''}`, { type: 'warning' });
                redirect('list', 'rewards');
                return;
            }
        }
        notify('Награда создана', { type: 'success' });
        redirect('list', 'rewards');
    };

    return (
        <>
            <QuestSectionTabs />
            <Create title="Квесты" transform={transform} mutationOptions={{ onSuccess }}>
                <RewardForm isCreate />
            </Create>
        </>
    );
};

// Редактирование: убранные картинки удаляем из S3, новые докладываем со следующими
// индексами, итоговый список уходит на бэкенд (image_url = первая — считает dataProvider).
const rewardEditTransform = async (data) => {
    const { __new_image_files = [], __removed_urls = [], ...rest } = data;

    await Promise.all(__removed_urls.map(async (url) => {
        try {
            await deleteFileFromS3(url);
        } catch (error) {
            console.error('Не удалось удалить изображение награды из S3:', error);
        }
    }));

    const urls = (rest.image_urls || []).filter(Boolean);
    let index = nextImageIndex([...urls, ...__removed_urls]);
    for (const file of __new_image_files) {
        // eslint-disable-next-line no-await-in-loop
        urls.push(await uploadQuestRewardImage(file, data.id, index));
        index += 1;
    }
    rest.image_urls = urls;
    return rest;
};

export const RewardEdit = () => (
    <>
        <QuestSectionTabs />
        <Edit title="Квесты" transform={rewardEditTransform} mutationMode="pessimistic">
            <RewardForm />
        </Edit>
    </>
);
